import React, { Component } from 'react'
import {
	TrendingUpIcon,
	TrendingDownIcon,
	RefreshIcon,
	ChartBarIcon } from '@heroicons/react/outline'
import InfoTip from './InfoTip'

function fmt(n)
{
	if (n === null || n === undefined)
		return '0'
	return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

// Libellé de période pour le sous-titre
const PERIOD_LABELS = {
	7: 'sur les 7 derniers jours',
	30: 'sur les 30 derniers jours',
	90: 'sur les 90 derniers jours',
	0: 'depuis le lancement'
}

class StatsTrendCard extends Component {
	// Graphe en aire, SVG inline responsive (viewBox + w-full). Pas de librairie.
	_chart = (points) => {
		const w = 600
		const h = 170
		const padLeft = 34
		const padRight = 10
		const padTop = 16
		const padBottom = 26
		const innerW = w - padLeft - padRight
		const innerH = h - padTop - padBottom
		const values = points.map(p => p.value)
		const max = Math.max.apply(null, values.concat([1]))
		const step = (points.length > 1) ? innerW / (points.length - 1) : 0
		const coords = points.map((p, i) => {
			const x = Math.round(padLeft + i * step)
			const y = Math.round(padTop + innerH - (p.value / max) * innerH)
			return { x: x, y: y, value: p.value, label: p.label }
		})
		const line = coords.map(c => c.x + ',' + c.y).join(' ')
		const baseY = padTop + innerH
		const area = padLeft + ',' + baseY + ' ' + line + ' ' + (padLeft + innerW) + ',' + baseY
		// Point culminant
		let peak = coords[0]
		for (let i = 1; i < coords.length; i++)
		{
			if (coords[i].value > peak.value)
				peak = coords[i]
		}
		// Étiquettes d'axe X : ~5 réparties (première et dernière incluses)
		const labelCount = Math.min(points.length, 5)
		const xLabels = []
		if (points.length > 0)
		{
			const lstep = (labelCount > 1) ? (points.length - 1) / (labelCount - 1) : 0
			for (let i = 0; i < labelCount; i++)
			{
				const idx = Math.round(i * lstep)
				xLabels.push(coords[idx])
			}
		}
		// Axe Y : 3 graduations (0, milieu, max) avec ligne de repère + valeur
		const yTicks = [0, 0.5, 1].map(r => ({
			y: Math.round(padTop + innerH - r * innerH),
			value: Math.round(max * r)
		}))
		return (
			<svg
				viewBox={'0 0 ' + w + ' ' + h}
				className="w-full"
				style={{ height: 'auto' }}
				role="img"
				aria-label={'Évolution des visiteurs ' + (PERIOD_LABELS[this.props.days] || '') + '. Pic de ' + peak.value + ' visiteurs.'}
			>
				{yTicks.map((t, i) => (
					<g key={i}>
						<line x1={padLeft} y1={t.y} x2={padLeft + innerW} y2={t.y} stroke="#f1f5f9" strokeWidth="1" vectorEffect="non-scaling-stroke" />
						<text x={padLeft - 6} y={t.y + 3} textAnchor="end" fontSize="10" fontWeight="700" fill="#cbd5e1">{fmt(t.value)}</text>
					</g>
				))}
				<polygon points={area} fill="#eef2ff" />
				<polyline points={line} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
				<circle cx={peak.x} cy={peak.y} r="4" fill="#4f46e5" stroke="#ffffff" strokeWidth="2" />
				{xLabels.map((c, i) => (
					<text key={i} x={c.x} y={h - 8} textAnchor={(i === 0) ? 'start' : ((i === xLabels.length - 1) ? 'end' : 'middle')} fontSize="11" fontWeight="700" fill="#94a3b8">{c.label}</text>
				))}
			</svg>
		)
	}

	render() {
		if (this.props.loading === true)
		{
			return (
				<div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 animate-pulse h-full">
					<div className="h-3 bg-slate-200 rounded-full w-1/3 mb-5"></div>
					<div className="h-40 bg-slate-100 rounded-xl"></div>
				</div>
			)
		}
		if (this.props.error === true || !this.props.trend)
		{
			return (
				<div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 h-full">
					<h2 className="text-2xs font-black text-slate-400 uppercase tracking-widest mt-0 mb-2">Tendance des visiteurs</h2>
					<p className="text-sm font-medium text-slate-500 mt-0 mb-3">Données indisponibles.</p>
					<button
						type="button"
						onClick={this.props.onRetry}
						className="inline-flex items-center gap-1.5 px-4 py-2 text-2xs font-black uppercase tracking-widest rounded-xl text-indigo-700 cursor-pointer transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-500"
						style={{ backgroundImage: 'none', backgroundColor: '#eef2ff' }}
					>
						<RefreshIcon className="w-4 h-4" aria-hidden="true" />
						Réessayer
					</button>
				</div>
			)
		}
		const points = (this.props.trend.points && this.props.trend.points.length > 0) ? this.props.trend.points : []
		const delta = (this.props.trend.delta === null || this.props.trend.delta === undefined) ? null : this.props.trend.delta
		let deltaPill = null
		if (delta !== null)
		{
			const pct = Math.round(delta * 100)
			const up = pct >= 0
			deltaPill = (
				<span className={'inline-flex items-center gap-0.5 text-2xs font-black px-2 py-0.5 rounded-full ' + (up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')}>
					{up
						? <TrendingUpIcon className="w-3 h-3" aria-hidden="true" />
						: <TrendingDownIcon className="w-3 h-3" aria-hidden="true" />}
					{(up ? '+' : '') + pct}%
				</span>
			)
		}
		return (
			<div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 h-full flex flex-col">
				<div className="flex items-center justify-between gap-3 mb-1">
					<span className="inline-flex items-center gap-1">
						<h2 className="text-2xs font-black text-slate-400 uppercase tracking-widest my-0">Tendance des visiteurs</h2>
						<InfoTip text="Évolution du nombre de visiteurs uniques par jour. Le pourcentage compare à la période précédente." />
					</span>
					{deltaPill}
				</div>
				<p className="text-xs font-medium text-slate-400 my-0 mb-4">Visiteurs {PERIOD_LABELS[this.props.days] || ''}{(delta !== null) ? ', comparé à la période précédente.' : '.'}</p>
				{points.length === 0
					? (
						<div className="flex-1 flex items-center gap-3">
							<div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
								<ChartBarIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
							</div>
							<p className="text-sm font-medium text-slate-500 my-0">Aucune visite sur cette période.</p>
						</div>
					)
					: (
						<div className="flex-1 flex items-center">{this._chart(points)}</div>
					)
				}
			</div>
		)
	}
}

export default StatsTrendCard
